import { keepPreviousData, queryOptions, skipToken } from '@tanstack/react-query';
import type { DataTag, QueryKey, UndefinedInitialDataOptions } from '@tanstack/react-query';

import { fetchScadaAnalysis } from '../api/analysis.api';
import {
  fetchLibraryHistory,
  fetchLibraryList,
  fetchLibraryType,
  fetchNodeTypes,
  fetchPlantTypes,
  fetchReadings,
} from '../api/library.api';
import { fetchRegistry, fetchRegistryEntry } from '../api/registry.api';
import { isNoLayout, isNotFound } from '../domain/failures';
import type { NameIndex, TierIndex } from '../domain/lookups';
import type { LibraryHistory, LibraryType, LibraryTypeSummary, NodeTypeOption, ScadaAnalysis } from '../types/contract';

import { CTL_CONFIG_GC, CTL_CONFIG_STALE, ctlConfigKeys } from './query-keys';
import type { RegistryListFilters } from './query-keys';

export type AnalysisRead = { readonly kind: 'NO_LAYOUT' } | { readonly kind: 'READY'; readonly analysis: ScadaAnalysis };

export type CtlQueryOptions<TQueryFnData, TData, TKey extends QueryKey> = UndefinedInitialDataOptions<TQueryFnData, Error, TData, TKey> & {
  queryKey: DataTag<TKey, TQueryFnData>;
};

type NodeTypesKey = ReturnType<typeof ctlConfigKeys.nodeTypes>;

type LibraryListKey = ReturnType<typeof ctlConfigKeys.library.lists>;

const readTypeOrNone = async (code: string, signal: AbortSignal): Promise<LibraryType | null> => {
  try {
    return await fetchLibraryType(code, signal);
  } catch (error) {
    if (isNotFound(error)) return null;
    throw error;
  }
};

const readHistoryOrEmpty = async (code: string, signal: AbortSignal): Promise<LibraryHistory> => {
  try {
    return await fetchLibraryHistory(code, signal);
  } catch (error) {
    if (isNotFound(error)) return { code, versions: [] };
    throw error;
  }
};

const readAnalysis = async (plantId: string): Promise<AnalysisRead> => {
  try {
    return { kind: 'READY', analysis: await fetchScadaAnalysis(plantId) };
  } catch (error) {
    if (isNoLayout(error)) return { kind: 'NO_LAYOUT' };
    throw error;
  }
};

export const libraryListQueryOptions = (canView: boolean) =>
  queryOptions({
    queryKey: ctlConfigKeys.library.lists(),
    queryFn: canView ? ({ signal }) => fetchLibraryList(signal) : skipToken,
    staleTime: CTL_CONFIG_STALE.libraryList,
    gcTime: CTL_CONFIG_GC.standard,
  });

export const libraryTypeQueryOptions = (code: string | undefined, canView: boolean) =>
  queryOptions({
    queryKey: ctlConfigKeys.library.detail(code ?? ''),
    queryFn: code && canView ? ({ signal }) => readTypeOrNone(code, signal) : skipToken,
    staleTime: CTL_CONFIG_STALE.libraryType,
    gcTime: CTL_CONFIG_GC.standard,
  });

export const typeHistoryQueryOptions = (code: string | undefined, canView: boolean) =>
  queryOptions({
    queryKey: ctlConfigKeys.library.history(code ?? ''),
    queryFn: code && canView ? ({ signal }) => readHistoryOrEmpty(code, signal) : skipToken,
    staleTime: CTL_CONFIG_STALE.history,
    gcTime: CTL_CONFIG_GC.standard,
  });

export const readingsQueryOptions = (canView: boolean) =>
  queryOptions({
    queryKey: ctlConfigKeys.readings(),
    queryFn: canView ? ({ signal }) => fetchReadings(signal) : skipToken,
    staleTime: CTL_CONFIG_STALE.readings,
    gcTime: CTL_CONFIG_GC.readings,
  });

export const plantTypesQueryOptions = (canView: boolean) =>
  queryOptions({
    queryKey: ctlConfigKeys.plantTypes(),
    queryFn: canView ? ({ signal }) => fetchPlantTypes(signal) : skipToken,
    staleTime: CTL_CONFIG_STALE.plantTypes,
    gcTime: CTL_CONFIG_GC.standard,
  });

export const nodeTypesQueryOptions = (canView: boolean) =>
  queryOptions({
    queryKey: ctlConfigKeys.nodeTypes(),
    queryFn: canView ? ({ signal }) => fetchNodeTypes(signal) : skipToken,
    staleTime: CTL_CONFIG_STALE.nodeTypes,
    gcTime: CTL_CONFIG_GC.standard,
  });

export const nodeTypeNamesQueryOptions = (canView: boolean): CtlQueryOptions<NodeTypeOption[], NameIndex, NodeTypesKey> =>
  queryOptions({
    queryKey: ctlConfigKeys.nodeTypes(),
    queryFn: canView ? ({ signal }) => fetchNodeTypes(signal) : skipToken,
    staleTime: CTL_CONFIG_STALE.nodeTypes,
    gcTime: CTL_CONFIG_GC.standard,
    select: (): NameIndex => new Map(),
  });

export const libraryTierIndexQueryOptions = (canView: boolean): CtlQueryOptions<LibraryTypeSummary[], TierIndex, LibraryListKey> =>
  queryOptions({
    queryKey: ctlConfigKeys.library.lists(),
    queryFn: canView ? ({ signal }) => fetchLibraryList(signal) : skipToken,
    staleTime: CTL_CONFIG_STALE.libraryList,
    gcTime: CTL_CONFIG_GC.standard,
    select: (): TierIndex => new Map(),
  });

export interface AnalysisGate {
  readonly requested: boolean;
  readonly canPlant: boolean;
}

export const scadaAnalysisQueryOptions = (plantId: string | undefined, { requested, canPlant }: AnalysisGate) =>
  queryOptions({
    queryKey: ctlConfigKeys.analysis(plantId ?? ''),
    queryFn: plantId && requested && canPlant ? () => readAnalysis(plantId) : skipToken,
    staleTime: CTL_CONFIG_STALE.analysis,
    gcTime: CTL_CONFIG_GC.analysis,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

export const registryListQueryOptions = (plantId: string | undefined, filters: RegistryListFilters, canPlant: boolean) =>
  queryOptions({
    queryKey: ctlConfigKeys.registry.list(plantId ?? '', filters),
    queryFn: plantId && canPlant ? ({ signal }) => fetchRegistry(plantId, filters, signal) : skipToken,
    staleTime: CTL_CONFIG_STALE.registryList,
    gcTime: CTL_CONFIG_GC.standard,
    placeholderData: keepPreviousData,
  });

export const registryEntryQueryOptions = (plantId: string | undefined, entryId: string | undefined, canPlant: boolean) =>
  queryOptions({
    queryKey: ctlConfigKeys.registry.detail(plantId ?? '', entryId ?? ''),
    queryFn: plantId && entryId && canPlant ? ({ signal }) => fetchRegistryEntry(plantId, entryId, signal) : skipToken,
    staleTime: CTL_CONFIG_STALE.registryEntry,
    gcTime: CTL_CONFIG_GC.standard,
  });
